import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';
import {
    GET_WHATSAPP_TEMPLATES,
    SEND_WHATSAPP_TEMPLATE
} from '../../utils/whatsappHelper';

const SendWhatsAppModal = ({ show, onHide, student, client }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [templateVariables, setTemplateVariables] = useState([]);
    const [templateBodyText, setTemplateBodyText] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Must match Communications Center — use Cookies.get('id') exactly
    const employeeId = parseInt(Cookies.get('id'));

    useEffect(() => {
        if (show && employeeId) {
            setError('');
            setSuccess(false);
            setSelectedTemplate('');
            setTemplateVariables([]);
            setTemplateBodyText('');
            fetchTemplates();
        }
    }, [show]);

    const fetchTemplates = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await client.query({
                query: GET_WHATSAPP_TEMPLATES,
                variables: { employee_id: employeeId },
                fetchPolicy: 'network-only'
            });
            setTemplates(result.data.getWhatsAppTemplates?.data || []);
        } catch (err) {
            setError("Failed to load templates.");
        } finally {
            setLoading(false);
        }
    };

    const parseTemplateComponents = (components) => {
        if (!components) return { bodyText: '', paramCount: 0 };
        try {
            const comps = typeof components === 'string' ? JSON.parse(components) : components;
            if (!Array.isArray(comps)) return { bodyText: '', paramCount: 0 };
            const bodyComp = comps.find(c => c.type === 'BODY');
            if (!bodyComp?.text) return { bodyText: '', paramCount: 0 };
            const matches = bodyComp.text.match(/\{\{\d+\}\}/g);
            return { bodyText: bodyComp.text, paramCount: matches ? matches.length : 0 };
        } catch { return { bodyText: '', paramCount: 0 }; }
    };

    const AUTO_FILL_LABELS = [
        { label: 'Student Name', hint: 'e.g. John Doe' },
        { label: 'Mobile', hint: 'e.g. 9999999999' },
        { label: 'Email', hint: 'e.g. john@example.com' },
        { label: 'Date', hint: 'e.g. 20th March 2025' },
        { label: 'Time', hint: 'e.g. 10:00 AM' },
        { label: 'Location', hint: 'e.g. KPHB Branch' },
    ];

    const handleTemplateChange = (e) => {
        const tplId = e.target.value;
        setSelectedTemplate(tplId);
        setError('');
        if (!tplId) { setTemplateVariables([]); setTemplateBodyText(''); return; }
        const template = templates.find(t => String(t.id) === String(tplId));
        if (template) {
            const { bodyText, paramCount } = parseTemplateComponents(template.components);
            setTemplateBodyText(bodyText);
            setTemplateVariables(Array.from({ length: paramCount }, (_, i) => ({
                placeholder: `{{${i + 1}}}`,
                label: AUTO_FILL_LABELS[i]?.label || `Var ${i + 1}`,
                hint: AUTO_FILL_LABELS[i]?.hint || '',
                value: i === 0 ? (student?.student_name || '') : '',
            })));
        }
    };

    const buildPreview = () => {
        if (!templateBodyText) return '';
        let preview = templateBodyText;
        templateVariables.forEach((v, i) => {
            preview = preview.replace(`{{${i + 1}}}`, v.value || `{{${i + 1}}}`);
        });
        return preview;
    };

    const handleSend = async () => {
        if (!selectedTemplate || !student?.student_mobile) return;
        setSending(true);
        setError('');
        try {
            const vars = templateVariables.length > 0 ? templateVariables.map(v => v.value || '') : null;
            const result = await client.mutate({
                mutation: SEND_WHATSAPP_TEMPLATE,
                variables: {
                    employee_id: employeeId,
                    mobile: student.student_mobile,
                    name: student.student_name,
                    template_id: parseInt(selectedTemplate),
                    ...(vars ? { variables: vars } : {})
                }
            });
            if (result.data.sendWhatsAppTemplate?.success) {
                setSuccess(true);
            } else {
                setError(result.data.sendWhatsAppTemplate?.message || "Failed to send.");
            }
        } catch (err) {
            setError("Error: " + err.message.replace("GraphQL error: ", ""));
        } finally {
            setSending(false);
        }
    };

    const handleClose = () => {
        setSuccess(false); setError(''); setSelectedTemplate('');
        setTemplateVariables([]); setTemplateBodyText('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" dialogClassName="af-premium-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fab fa-whatsapp" /> Send WhatsApp Template
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <div className="alert alert-danger d-flex align-items-center py-2 mb-4" style={{ borderRadius: '8px', fontSize: '13px' }}>
                        <i className="fas fa-exclamation-circle mr-2" />{error}
                    </div>
                )}

                {success ? (
                    /* ── Success State ── */
                    <div className="text-center py-4">
                        <div className="af-modal-student-summary justify-content-center mb-4" style={{ flexDirection: 'column', gap: '16px' }}>
                            <div style={{
                                width: '64px', height: '64px', borderRadius: '18px',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto', boxShadow: '0 8px 20px rgba(16,185,129,0.3)'
                            }}>
                                <i className="fas fa-check" style={{ fontSize: '26px', color: '#fff' }} />
                            </div>
                            <div>
                                <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Message Sent!</h5>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                    WhatsApp template sent to <strong>{student?.student_name}</strong>
                                </p>
                            </div>
                        </div>
                        <button className="btn btn-outline-secondary" style={{ borderRadius: '10px', height: '40px', minWidth: '120px', fontWeight: '500' }} onClick={handleClose}>
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        {/* ── Recipient ── */}
                        <div className="af-modal-student-summary">
                            <div className="af-summary-avatar">
                                {(student?.student_name || 'S')[0].toUpperCase()}
                            </div>
                            <div className="af-summary-text">
                                <strong>{student?.student_name}</strong>
                                <span className="text-muted" style={{ fontSize: '13px' }}>
                                    <i className="fas fa-phone-alt mr-1" />{student?.student_mobile}
                                </span>
                            </div>
                            <span style={{
                                marginLeft: 'auto', padding: '4px 12px', borderRadius: '20px',
                                background: '#dcfce7', color: '#15803d',
                                fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px'
                            }}>
                                <i className="fab fa-whatsapp" /> WhatsApp
                            </span>
                        </div>

                        {/* ── Template selector ── */}
                        <Form.Group className="mb-4">
                            <Form.Label className="af-field-label">Select Template</Form.Label>
                            {loading ? (
                                <div className="d-flex align-items-center text-muted" style={{ gap: '8px', fontSize: '13px', height: '42px' }}>
                                    <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                                    Loading templates...
                                </div>
                            ) : (
                                <Form.Control
                                    as="select"
                                    className="af-form-select"
                                    value={selectedTemplate}
                                    onChange={handleTemplateChange}
                                    disabled={templates.length === 0}
                                >
                                    <option value="">{templates.length === 0 ? '— No templates available —' : '— Select a template —'}</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.template_name}</option>
                                    ))}
                                </Form.Control>
                            )}
                        </Form.Group>

                        {/* ── Preview + Variables ── */}
                        {selectedTemplate ? (
                            <Row>
                                <Col lg={templateVariables.length > 0 ? 6 : 12}>
                                    <Form.Label className="af-field-label">Message Preview</Form.Label>
                                    <div style={{
                                        background: '#f0fdf4', border: '1px solid #bbf7d0',
                                        borderRadius: '10px', padding: '16px', minHeight: '140px',
                                        fontSize: '14px', lineHeight: '1.7', color: '#15803d',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {buildPreview() || <span className="text-muted" style={{ fontStyle: 'italic' }}>[No content]</span>}
                                    </div>
                                    <small className="text-muted mt-1 d-block">Variables shown substituted in preview.</small>
                                </Col>
                                {templateVariables.length > 0 && (
                                    <Col lg={6}>
                                        <Form.Label className="af-field-label">Fill Variables</Form.Label>
                                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                                            {templateVariables.map((v, i) => (
                                                <div key={i} className={i < templateVariables.length - 1 ? 'mb-3' : ''}>
                                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px', display: 'block' }}>
                                                        <code style={{ background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', marginRight: '5px' }}>
                                                            {v.placeholder}
                                                        </code>
                                                        {v.label}
                                                    </label>
                                                    <Form.Control
                                                        type="text"
                                                        size="sm"
                                                        placeholder={v.hint}
                                                        value={v.value}
                                                        onChange={(e) => {
                                                            const updated = [...templateVariables];
                                                            updated[i] = { ...updated[i], value: e.target.value };
                                                            setTemplateVariables(updated);
                                                        }}
                                                        className="af-form-select"
                                                        style={{ height: '36px', fontSize: '13px' }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        ) : (
                            !loading && (
                                <div className="text-center py-4 text-muted">
                                    <i className="fab fa-whatsapp mb-3" style={{ fontSize: '40px', opacity: 0.2, display: 'block' }} />
                                    <p style={{ fontSize: '14px' }}>Select a template to compose your message.</p>
                                </div>
                            )
                        )}
                    </>
                )}
            </Modal.Body>

            {!success && (
                <Modal.Footer style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <button className="af-cancel-btn" onClick={handleClose}>Cancel</button>
                    <button
                        className="af-save-btn"
                        onClick={handleSend}
                        disabled={sending || !selectedTemplate}
                        style={{ minWidth: '160px' }}
                    >
                        {sending
                            ? <><Spinner animation="border" size="sm" className="mr-2" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />Sending...</>
                            : <><i className="fab fa-whatsapp mr-2" />Send WhatsApp</>
                        }
                    </button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default withApollo(SendWhatsAppModal);
